// ✅ Expert tier system for labeling and analytics (not access gating)
import { collection, query, where, getDocs, limit } from "firebase/firestore";
import { getAuth } from "firebase/auth";
import { db } from "./firebase";

// Expert tier definitions
export const EXPERT_TIERS = {
  GENERAL: 'general',
  VERIFIED: 'verified',
  PREMIUM: 'premium'
};

// Plan access mapping - all plans can access all expert tiers
export const PLAN_EXPERT_ACCESS = {
  'free': [EXPERT_TIERS.GENERAL, EXPERT_TIERS.VERIFIED, EXPERT_TIERS.PREMIUM],
  'starter_monthly': [EXPERT_TIERS.GENERAL, EXPERT_TIERS.VERIFIED, EXPERT_TIERS.PREMIUM],
  'pro_monthly': [EXPERT_TIERS.GENERAL, EXPERT_TIERS.VERIFIED, EXPERT_TIERS.PREMIUM],
  'unlimited_monthly': [EXPERT_TIERS.GENERAL, EXPERT_TIERS.VERIFIED, EXPERT_TIERS.PREMIUM]
};

// Get available expert tiers for a user's plan
export const getAvailableExpertTiers = (userData) => {
  const planId = userData?.planId || 'free';
  return PLAN_EXPERT_ACCESS[planId] || [EXPERT_TIERS.GENERAL];
};

// Check if user can access premium experts
export const canAccessPremiumExperts = (userData) => {
  return true;
};

// Check if user can access verified experts
export const canAccessVerifiedExperts = (userData) => {
  const availableTiers = getAvailableExpertTiers(userData);
  return availableTiers.includes(EXPERT_TIERS.VERIFIED);
};

// Determine expert tier based on profile data
export const getExpertTier = (expertData) => {
  // Check for premium indicators (C-level, founders, VPs, directors)
  const premiumKeywords = [
    'ceo', 'cto', 'cfo', 'coo', 'founder', 'co-founder', 'president',
    'vp', 'vice president', 'director', 'head of', 'chief',
    'partner', 'managing director', 'executive director', 'general manager'
  ];

  const verifiedKeywords = [
    'senior', 'lead', 'principal', 'manager', 'consultant', 'specialist',
    'architect', 'engineer', 'analyst', 'coordinator'
  ];

  // ✅ FIXED: Only check TITLE field for tier classification (not bio or expertise)
  // This prevents false positives from bio content like "advise executive teams"
  const title = (expertData.title || expertData.expertProfile?.title || '').toLowerCase();

  // Premium tier check - C-level, founders, VPs, directors only
  const premiumMatch = premiumKeywords.some(keyword => {
    const regex = new RegExp(`\\b${keyword.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`, 'i');
    return regex.test(title);
  });

  if (premiumMatch) {
    return EXPERT_TIERS.PREMIUM;
  }

  // Verified tier check - check title for verified keywords OR other verified indicators
  const hasVerifiedKeywords = verifiedKeywords.some(keyword => {
    const regex = new RegExp(`\\b${keyword.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`, 'i');
    return regex.test(title);
  });

  if (hasVerifiedKeywords ||
      expertData.verified ||
      expertData.expertProfile?.verified ||
      (expertData.rating && expertData.rating >= 4.5)) {
    return EXPERT_TIERS.VERIFIED;
  }

  // Default to general
  return EXPERT_TIERS.GENERAL;
};

// Filter experts based on user's plan access
export const filterExpertsByPlan = (experts, userData) => {
  const availableTiers = getAvailableExpertTiers(userData);
  
  return experts.filter(expert => {
    const expertTier = getExpertTier(expert);
    return availableTiers.includes(expertTier);
  });
};

// Load experts (tier remains for metadata; no plan-based filtering)
export const loadExpertsForUser = async (userData, limit_count = null) => {
  try {
    console.log('🔍 Loading experts for user plan:', userData?.planId);

    const auth = getAuth();
    const isAuthenticated = !!auth.currentUser;

    let expertsData = [];

    if (isAuthenticated) {
      // Authenticated: include approved mavens plus photo-backed new mavens.
      const constraints = [
        where('role', '==', 'expert')
      ];
      if (limit_count) constraints.push(limit(limit_count));

      const expertsQuery = query(collection(db, 'users'), ...constraints);
      const expertsSnapshot = await getDocs(expertsQuery);

      expertsData = expertsSnapshot.docs.map(doc => {
        const data = doc.data();
        return {
          uid: doc.id,
          id: doc.id,
          ...data,
          tier: getExpertTier(data)
        };
      });
    } else {
      // Unauthenticated: fall back to public `experts` collection
      const expertsQuery = limit_count
        ? query(collection(db, 'experts'), limit(limit_count))
        : query(collection(db, 'experts'));
      const expertsSnapshot = await getDocs(expertsQuery);

      expertsData = expertsSnapshot.docs.map(doc => {
        const data = doc.data();
        return {
          uid: doc.id,
          id: doc.id,
          ...data,
          tier: getExpertTier(data)
        };
      });
    }

    const hasProfilePhoto = (expert) => {
      const photo = expert.photos?.[0]?.thumbnail || expert.photos?.[0]?.url ||
        expert.photoThumbnailURL || expert.photoURL || expert.expertProfile?.photoURL;
      return typeof photo === 'string' &&
        /firebasestorage\.googleapis\.com|storage\.googleapis\.com/i.test(photo);
    };

    // Anonymous visitors see approved profiles only. Signed-in users can also
    // discover new, not-submitted mavens, but every directory entry needs a photo.
    // NOTE: the authenticated path already queries users for
    // applicationStatus == 'approved', but the anonymous path reads the public
    // `experts` collection, which ALSO holds never-approved docs (missing
    // applicationStatus, or not_submitted/pending). Strict equality here — a
    // missing field is NOT approved — so logged-out visitors never see them.
    expertsData = expertsData.filter(expert => {
      const name = (expert.displayName || expert.name || '').toLowerCase();
      const visibleStatus = expert.applicationStatus === 'approved' ||
        (isAuthenticated && expert.role === 'expert' && expert.applicationStatus === 'not_submitted');
      return visibleStatus && hasProfilePhoto(expert) &&
        expert.accountDisabled !== true &&
        expert.accountStatus !== 'deleted' &&
        expert.accountStatus !== 'deactivated' &&
        expert.accountStatus !== 'pending_deletion' &&
        expert.applicationStatus !== 'deleted' &&
        expert.isActive !== false &&
        !expert.deletionRequestedAt &&
        !expert.isDeleted &&
        !name.includes('[deleted]');
    });

    return { experts: expertsData };

  } catch (error) {
    console.error('❌ Error loading experts:', error);
    throw error;
  }
};

// Get expert tier display info
export const getExpertTierInfo = (tier) => {
  const tierInfo = {
    [EXPERT_TIERS.GENERAL]: {
      name: 'General Expert',
      badge: '👤',
      color: '#718096',
      description: 'Qualified professional with relevant experience'
    },
    [EXPERT_TIERS.VERIFIED]: {
      name: 'Verified Expert',
      badge: '✅',
      color: '#38A169',
      description: 'Experienced professional with verified credentials'
    },
    [EXPERT_TIERS.PREMIUM]: {
      name: 'Premium Expert',
      badge: '⭐',
      color: '#D69E2E',
      description: 'Senior executive, founder, or industry leader'
    }
  };
  
  return tierInfo[tier] || tierInfo[EXPERT_TIERS.GENERAL];
};

// Check if user can access a specific expert
export const canAccessExpert = (expertData, userData) => {
  const expertTier = getExpertTier(expertData);
  const availableTiers = getAvailableExpertTiers(userData);
  return availableTiers.includes(expertTier);
};

// Get upgrade message for accessing restricted experts
export const getUpgradeMessage = (expertTier, currentPlan) => {
  return null;
};

// Get tier distribution for analytics
export const getExpertTierDistribution = (experts) => {
  const distribution = {
    [EXPERT_TIERS.GENERAL]: 0,
    [EXPERT_TIERS.VERIFIED]: 0,
    [EXPERT_TIERS.PREMIUM]: 0
  };
  
  experts.forEach(expert => {
    const tier = getExpertTier(expert);
    distribution[tier]++;
  });
  
  return distribution;
};

// Update expert tier in database (admin function)
export const updateExpertTier = async (expertId, tier) => {
  try {
    await updateDoc(doc(db, "users", expertId), {
      'expertProfile.tier': tier,
      'expertProfile.tierUpdated': new Date().toISOString()
    });
    
    console.log('✅ Expert tier updated:', expertId.slice(-6), '→', tier);
    return true;
  } catch (error) {
    console.error('❌ Error updating expert tier:', error);
    return false;
  }
};
